import { Injectable } from '@angular/core';
import { handleGridTab, focusGridInput, GridTabConfig } from './grid-tab-helper';

/**
 * Grid Tab 方向管理服務
 * 管理 Tab 鍵在 Grid 編輯模式的導航方向，並提供整合的導航處理
 *
 * 使用 providedIn: 'root' 確保單例，跨頁面保留狀態
 */
@Injectable({ providedIn: 'root' })
export class GridTabDirectionService {
  /** Tab 方向：'down' 往下優先，'right' 使用瀏覽器預設 */
  private direction: 'down' | 'right' = 'right';  // 預設向右

  /**
   * 取得當前 Tab 方向
   */
  getDirection(): 'down' | 'right' {
    return this.direction;
  }

  /**
   * 設定 Tab 方向
   */
  setDirection(value: 'down' | 'right'): void {
    this.direction = value;
  }

  /**
   * 是否為向下模式
   */
  isDownDirection(): boolean {
    return this.direction === 'down';
  }

  /**
   * 處理 Tab 導航（整合方向設定、計算、聚焦）
   *
   * 用法：
   * ```typescript
   * onInputKeydown(event: KeyboardEvent, item: MyDto, field: string) {
   *   if (event.key !== 'Tab') return;
   *
   *   this.gridTabDirectionService.handleTabNavigation(event, {
   *     items: this.filteredItems,
   *     currentItem: item,
   *     currentField: field,
   *     fields: ['qty', 'cost'],
   *     getId: (i) => i.Id,
   *     isEditable: (i) => !i.IsOpen
   *   });
   * }
   * ```
   *
   * @param event 鍵盤事件
   * @param config 導航配置（不需要傳入 direction，會自動使用 Service 的設定）
   * @returns true 表示有處理（向下模式），false 表示未處理（向右模式）
   */
  handleTabNavigation<T>(event: KeyboardEvent, config: Omit<GridTabConfig<T>, 'direction'>): boolean {
    const result = handleGridTab(event, {
      ...config,
      direction: this.direction
    });

    if (result) {
      event.preventDefault();
      focusGridInput(result.itemId, result.field);
      return true;
    }
    return false;
  }
}
